import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import AppBrandMark from '../AppBrandMark';
import MfaEnrollmentPanel from './MfaEnrollmentPanel';
import { setSignOutReason } from '../../hooks/useSessionGuard';
import { auth } from '../../firebase';
import { readSessionClaims, secondFactorSatisfied } from '../../services/careAuthService';
import './Security.css';

/**
 * Full-screen gate: the session has no proven second factor and the account's
 * grace period (users/{uid}.mfaExemptUntil) is over. Firestore rules already
 * deny patient data in this state, so nothing else in the app would work.
 */
export default function ForceMfaEnrollmentScreen() {
  const { profile, signOut, refreshSession, isMicrosoftUser, mfaEnrolled } = useAuth();
  const [finishing, setFinishing] = useState(false);
  const [notice, setNotice] = useState('');

  const name = profile?.firstName || profile?.displayName || 'there';

  const handleEnrolled = async () => {
    setFinishing(true);
    // Enrollment returns fresh tokens that normally carry sign_in_second_factor.
    // If not, a fresh sign-in (which will now demand the factor) is the reliable path.
    let satisfied = false;
    try {
      satisfied = secondFactorSatisfied(await readSessionClaims(auth.currentUser, true));
    } catch {
      satisfied = false;
    }
    if (satisfied) {
      await refreshSession();
      return;
    }
    setSignOutReason('mfa-enrolled');
    await signOut();
  };

  const handleSignOutIn = async () => {
    setSignOutReason('mfa-enrolled');
    await signOut();
  };

  return (
    <div className="sec-screen">
      <div className="sec-card">
        <h1 className="sec-brand"><AppBrandMark size="login" /></h1>

        {isMicrosoftUser ? (
          <>
            <h2 className="sec-title">Sign in with Microsoft to continue</h2>
            <p className="sec-desc">
              Hi {name} — Trilogy One Care needs a sign-in verified by Microsoft and Duo for this session.
              Sign out and choose <strong>Sign in with Microsoft</strong> on the login page.
            </p>
            <button type="button" className="sec-btn sec-btn--primary" onClick={handleSignOutIn}>
              Sign out and sign in with Microsoft
            </button>
          </>
        ) : mfaEnrolled ? (
          <>
            <h2 className="sec-title">Sign in again to continue</h2>
            <p className="sec-desc">
              Two-step verification is set up on your account, but this session started without it.
              Sign in again and enter your verification code.
            </p>
            <button type="button" className="sec-btn sec-btn--primary" onClick={handleSignOutIn}>
              Sign out and sign in again
            </button>
          </>
        ) : (
          <>
            <h2 className="sec-title">Set up two-step verification</h2>
            <p className="sec-desc">
              Hi {name} — Trilogy One Care now requires a second step at sign-in to protect patient
              information. This takes about a minute and you only set it up once.
            </p>
            {notice && <div className="sec-success" role="status">{notice}</div>}
            {finishing ? (
              <p className="sec-desc">Finishing up…</p>
            ) : (
              <MfaEnrollmentPanel
                onEnrolled={async (kind) => {
                  setNotice(kind === 'phone' ? 'Text-message verification is on.' : 'Authenticator app is on.');
                  await handleEnrolled();
                }}
              />
            )}
          </>
        )}

        <div className="sec-footer">
          <button type="button" className="sec-link sec-link--muted" onClick={signOut}>Sign out</button>
          <span className="sec-desc" style={{ margin: 0, fontSize: '0.8rem' }}>
            Need help? Contact your administrator.
          </span>
        </div>
      </div>
    </div>
  );
}
